import { message } from 'antd';
import { getBackendUri } from '../utils/EnvConfigUtils';
import FetchUtils, { parseJSON } from './fetch_utils';
import posthog from 'posthog-js';
import { nullSafeGet } from '../utils/DataAccessUtils';

const fetchUtils = new FetchUtils();

export const owuploadCustomRequest = ({ userType, file, onError, onSuccess, onProgress }: any) => {
	const fileType = file.type;
	const fileName = file.name;

	const preSignedURLEndpoint = `${getBackendUri()}/api/v1/${userType}/file/upload/get_presigned_url`; // replace USER_TYPE

	fetchUtils
		.post(preSignedURLEndpoint, {
			data: {
				fileType,
				fileName,
			},
		})
		.then(parseJSON)
		.then(({ json }) => {
			const returnData = json.data;
			const signedRequest = returnData.presignedUrl;

			const uploadStartedAt = Date.now();

			// Use XMLHttpRequest for progress tracking
			const xhr = new XMLHttpRequest();

			// Track progress
			xhr.upload.onprogress = (event) => {
				if (event.lengthComputable) {
					const percent = Math.round((event.loaded / event.total) * 100);
					onProgress && onProgress({ percent });
				}
			};

			xhr.onload = () => {
				const durationMs = Date.now() - uploadStartedAt;
				if (xhr.status >= 200 && xhr.status < 300) {
					posthog.capture('file_upload_completed', {
						source: 'owuploadCustomRequest',
						file_name: fileName,
						file_type: fileType,
						file_size: nullSafeGet('size', file),
						duration_ms: durationMs,
					});
					onSuccess({
						data: {
							...returnData,
							fileUrl: returnData.fileUrl || returnData.url || signedRequest.split('?')[0], // fallback
						},
					});
				} else {
					const error = new Error(`Upload failed with status ${xhr.status}`);
					posthog.capture('file_upload_failed', {
						source: 'owuploadCustomRequest',
						file_name: fileName,
						file_type: fileType,
						file_size: nullSafeGet('size', file),
						duration_ms: durationMs,
						error: error.message,
					});
					onError && onError(error);
					message.error(`${fileName} upload failed: ${error.message}`);
				}
			};

			xhr.onerror = () => {
				const durationMs = Date.now() - uploadStartedAt;
				const error = new Error('Upload failed');
				posthog.capture('file_upload_failed', {
					source: 'owuploadCustomRequest',
					file_name: fileName,
					file_type: fileType,
					file_size: nullSafeGet('size', file),
					duration_ms: durationMs,
					error: error.message,
				});
				onError && onError(error);
				message.error(`${fileName} upload failed: ${error.message}`);
			};

			xhr.onabort = () => {
				const durationMs = Date.now() - uploadStartedAt;
				const error = new Error('Upload cancelled');
				posthog.capture('file_upload_cancelled', {
					source: 'owuploadCustomRequest',
					file_name: fileName,
					file_type: fileType,
					file_size: nullSafeGet('size', file),
					duration_ms: durationMs,
				});
				onError && onError(error);
			};

			xhr.open('PUT', signedRequest);
			xhr.setRequestHeader('Content-Type', fileType);
			xhr.send(file);
		})
		.catch((error) => {
			onError && onError(error);
			message.error(`File Upload Failed: ${JSON.stringify(error)}`);
		});
};

export const workOrderChatUploadCustomRequest = ({
	userType,
	workOrderId,
	file,
	onError,
	onSuccess,
	onProgress,
	source = 'workOrderChatUpload',
}: any) => {
	const fileType = file.type;
	const fileName = file.name;

	const preSignedURLEndpoint = `${getBackendUri()}/api/v1/${userType}/file/work_order_chat/upload/get_presigned_url/${workOrderId}`;

	fetchUtils
		.post(preSignedURLEndpoint, {
			data: {
				fileType,
				fileName,
			},
		})
		.then(parseJSON)
		.then(({ json }) => {
			const returnData = json.data;
			const signedRequest = returnData.presignedUrl;

			const uploadStartedAt = Date.now();

			// Use XMLHttpRequest for progress tracking
			const xhr = new XMLHttpRequest();

			xhr.upload.onprogress = (event) => {
				if (event.lengthComputable) {
					const percent = Math.round((event.loaded / event.total) * 100);
					onProgress && onProgress({ percent });
				}
			};

			xhr.onload = () => {
				const durationMs = Date.now() - uploadStartedAt;
				if (xhr.status >= 200 && xhr.status < 300) {
					posthog.capture('file_upload_completed', {
						source: source,
						file_name: fileName,
						file_type: fileType,
						file_size: nullSafeGet('size', file),
						duration_ms: durationMs,
					});
					onSuccess &&
						onSuccess({
							data: returnData.cloudFrontUrl,
						});
				} else {
					const error = new Error(`Upload failed with status ${xhr.status}`);
					posthog.capture('file_upload_failed', {
						source: source,
						file_name: fileName,
						file_type: fileType,
						file_size: nullSafeGet('size', file),
						duration_ms: durationMs,
						error: error.message,
					});
					onError && onError(error);
					message.error(`${fileName} upload failed: ${error.message}`);
				}
			};

			xhr.onerror = () => {
				const durationMs = Date.now() - uploadStartedAt;
				const error = new Error('Upload failed');
				posthog.capture('file_upload_failed', {
					source: source,
					file_name: fileName,
					file_type: fileType,
					file_size: nullSafeGet('size', file),
					duration_ms: durationMs,
					error: error.message,
				});
				onError && onError(error);
				message.error(`${fileName} upload failed: ${error.message}`);
			};

			xhr.onabort = () => {
				const durationMs = Date.now() - uploadStartedAt;
				const error = new Error('Upload cancelled');
				posthog.capture('file_upload_cancelled', {
					source: source,
					file_name: fileName,
					file_type: fileType,
					file_size: nullSafeGet('size', file),
					duration_ms: durationMs,
				});
				onError && onError(error);
			};

			xhr.open('PUT', signedRequest);
			xhr.setRequestHeader('Content-Type', fileType);
			xhr.send(file);
		})
		.catch((error) => {
			onError && onError(error);
			message.error(`File Upload Failed: ${JSON.stringify(error)}`);
		});
};

export const walkthroughNoteChatUploadCustomRequest = ({
	userType,
	walkthroughId,
	file,
	onError,
	onSuccess,
	onProgress,
	source = 'walkthroughNoteChatUpload',
}: any) => {
	const fileType = file.type;
	const fileName = file.name;

	const preSignedURLEndpoint = `${getBackendUri()}/api/v1/${userType}/file/walkthrough_note/upload/get_presigned_url/${walkthroughId}`;

	fetchUtils
		.post(preSignedURLEndpoint, {
			data: {
				fileType,
				fileName,
			},
		})
		.then(parseJSON)
		.then(({ json }) => {
			const returnData = json.data;
			const signedRequest = returnData.presignedUrl;

			const uploadStartedAt = Date.now();

			const xhr = new XMLHttpRequest();

			xhr.upload.onprogress = (event) => {
				if (event.lengthComputable) {
					const percent = Math.round((event.loaded / event.total) * 100);
					onProgress && onProgress({ percent });
				}
			};

			xhr.onload = () => {
				const durationMs = Date.now() - uploadStartedAt;
				if (xhr.status >= 200 && xhr.status < 300) {
					posthog.capture('file_upload_completed', {
						source: source,
						file_name: fileName,
						file_type: fileType,
						file_size: nullSafeGet('size', file),
						duration_ms: durationMs,
					});
					onSuccess &&
						onSuccess({
							data: returnData.cloudFrontUrl,
						});
				} else {
					const error = new Error(`Upload failed with status ${xhr.status}`);
					posthog.capture('file_upload_failed', {
						source: source,
						file_name: fileName,
						file_type: fileType,
						file_size: nullSafeGet('size', file),
						duration_ms: durationMs,
						error: error.message,
					});
					onError && onError(error);
					message.error(`${fileName} upload failed: ${error.message}`);
				}
			};

			xhr.onerror = () => {
				const durationMs = Date.now() - uploadStartedAt;
				const error = new Error('Upload failed');
				posthog.capture('file_upload_failed', {
					source: source,
					file_name: fileName,
					file_type: fileType,
					file_size: nullSafeGet('size', file),
					duration_ms: durationMs,
					error: error.message,
				});
				onError && onError(error);
				message.error(`${fileName} upload failed: ${error.message}`);
			};

			xhr.onabort = () => {
				const durationMs = Date.now() - uploadStartedAt;
				const error = new Error('Upload cancelled');
				posthog.capture('file_upload_cancelled', {
					source: source,
					file_name: fileName,
					file_type: fileType,
					file_size: nullSafeGet('size', file),
					duration_ms: durationMs,
				});
				onError && onError(error);
			};

			xhr.open('PUT', signedRequest);
			xhr.setRequestHeader('Content-Type', fileType);
			xhr.send(file);
		})
		.catch((error) => {
			onError && onError(error);
			message.error(`File Upload Failed: ${JSON.stringify(error)}`);
		});
};
